/**
 *
 * OverallTrendsSignedIn
 *
 */

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import _get from 'lodash/get';
import cn from 'classnames';
import { useHistory } from 'react-router-dom';
import { differenceInDays, add } from 'date-fns';
import _isEmpty from 'lodash/isEmpty';
import { useSelector } from 'react-redux';
import { useFirebase } from 'react-redux-firebase';
import { makeStyles } from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import ButtonBase from '@material-ui/core/ButtonBase';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import CircularProgress from '@material-ui/core/CircularProgress';
import useSiteCopySelector from 'components/useSiteCopySelector';
import { makeSelectAuth } from 'containers/Auth/selectors';
import Mixpanel from 'utils/mixpanelService';
import { toast } from 'react-toastify';
import Trends from './TrendsChart';
import useTrendsData from './hooks/useTrendsData';

const useStyles = makeStyles(theme => ({
  wrapper: {
    [theme.breakpoints.up('md')]: {
      paddingLeft: 16,
      paddingTop: 16,
      maxWidth: 340,
    },
  },
  header: {
    [theme.breakpoints.down('sm')]: {
      marginTop: 16,
    },
  },
  callToAction: {
    lineHeight: 1.4,
    fontSize: '1.05em',
    fontFamily: 'PoppinsSemiBold',
    fontWeight: 600,
    marginTop: 8,
    marginBottom: 8,
  },
  callToActionButton: {
    display: 'flex',
    padding: '12px 40px',
    borderRadius: 4,
    color: 'white',
    backgroundColor: '#414141',
    justifyContent: 'center',
    width: '100%',
    ...theme.typography.caption,
    '&:hover': {
      backgroundColor: '#4E4D4D',
    },
  },
  disabledCta: {
    opacity: 0.7,
  },
}));

function OverallTrendsSignedIn({
  assessment,
  component,
  slug,
  sortedIntervals,
  displayIntervals,
  scale,
  isNewLogin,
}) {
  const [isReminded, setIsReminded] = useState(isNewLogin);
  const [assessmentResultsSiteCopy] = useSiteCopySelector([
    'assessment-results',
  ]);
  const [isLoadingReminder, setIsLoadingReminder] = useState(false);
  const classes = useStyles();
  const history = useHistory();
  const auth = useSelector(makeSelectAuth());
  const firebase = useFirebase();
  const data = useTrendsData({
    slug: _get(assessment, 'fields.slug'),
    displayIntervals,
    component,
    type: 'overall',
  });

  if (_isEmpty(data)) return null;

  const retakeInterval = _get(
    assessment,
    'fields.globalOptions.trendsRetakeInterval',
  );

  const latest = data[data.length - 1];
  const diffDays = differenceInDays(Date.now(), latest.timestamp);
  const showCallToAction = !!retakeInterval;

  const handleRetakeAssessmentClick = () => {
    Mixpanel.track('Assessment Results Trend - Retake CTA Clicked', {
      assessment: _get(assessment, 'fields.name'),
      entryId: _get(assessment, 'sys.id'),
    });

    history.push(`/assessments/${slug}/page1`);
  };

  const handleRemindAssessmentClick = () => {
    setIsLoadingReminder(true);
    Mixpanel.track('Assessment Results Trend - Remind CTA Clicked', {
      assessment: _get(assessment, 'fields.name'),
      entryId: _get(assessment, 'sys.id'),
    });

    const dateToday = Date.now();
    const retakeDate = add(dateToday, {
      days: retakeInterval - diffDays,
    }).getTime();

    const reminderData = {
      email: _get(auth, 'email'),
      assessmentId: _get(assessment, 'sys.id'),
      assessment: _get(assessment, 'fields.name'),
      timestamp: dateToday,
      retakeDate,
      reminderSentDate: null,
    };

    firebase
      .firestore()
      .collection('assessment_reminder')
      .add(reminderData)
      .then(() => {
        const message = _get(
          assessmentResultsSiteCopy,
          'pageCopy.toastMessages.sendRetakeReminder',
          '',
        ).replace('#diffDays', retakeInterval - diffDays);

        toast(message, {
          type: 'success',
        });

        setIsReminded(true);
      })
      .catch(error => {
        const message = _get(error, 'message', '');
        toast(`An error has occured. ${message}`, {
          type: 'error',
        });
      })
      .finally(() => {
        setIsLoadingReminder(false);
      });
  };

  return (
    <Grid item xs={12} md="auto" className={classes.wrapper}>
      <Typography variant="h6" color="textPrimary" className={classes.header}>
        {_get(assessmentResultsSiteCopy, 'pageCopy.trendsHeader')}
      </Typography>
      <Box component="div">
        <Trends scale={scale} sortedIntervals={sortedIntervals} values={data} />
      </Box>
      {showCallToAction && (
        <>
          {diffDays > retakeInterval ? (
            <>
              <Typography color="textPrimary" className={classes.callToAction}>
                {_get(
                  assessmentResultsSiteCopy,
                  'pageCopy.trendsSignedCallToAction',
                )}
              </Typography>
              <ButtonBase
                className={classes.callToActionButton}
                disableRipple
                onClick={handleRetakeAssessmentClick}
              >
                {_get(assessmentResultsSiteCopy, 'pageCopy.retakeLabel')}
              </ButtonBase>
            </>
          ) : isReminded ? (
            <Typography color="textPrimary" className={classes.callToAction}>
              {_get(
                assessmentResultsSiteCopy,
                'pageCopy.toastMessages.sendRetakeReminder',
                '',
              ).replace('#diffDays', retakeInterval - diffDays)}
            </Typography>
          ) : (
            <>
              <Typography color="textPrimary" className={classes.callToAction}>
                {_get(
                  assessmentResultsSiteCopy,
                  'pageCopy.trendsSignedReminderLabel',
                  '',
                ).replace('#diffDays', retakeInterval - diffDays)}
              </Typography>
              <ButtonBase
                className={cn(classes.callToActionButton, {
                  [classes.disabledCta]: isLoadingReminder,
                })}
                disableRipple
                onClick={handleRemindAssessmentClick}
                disabled={isLoadingReminder}
              >
                {isLoadingReminder ? (
                  <CircularProgress size={22} color="secondary" />
                ) : (
                  _get(
                    assessmentResultsSiteCopy,
                    'pageCopy.trendsSignedCallToActionReminder',
                  )
                )}
              </ButtonBase>
            </>
          )}
        </>
      )}
    </Grid>
  );
}

OverallTrendsSignedIn.propTypes = {
  assessment: PropTypes.object,
  component: PropTypes.object,
  sortedIntervals: PropTypes.array,
  scale: PropTypes.number,
  slug: PropTypes.string,
  isNewLogin: PropTypes.bool,
};

export default OverallTrendsSignedIn;
